import { supabase } from './supabase';
import {
  DEFAULT_GAS_FAMILY_DATA,
  computeGasChannelMetrics,
  finiteOrNull,
  gasBlock,
  inferGasProfile,
  mergeGasFamilyData,
  validateGasRows,
} from './gas-calibration-profile';

export { getTodayDailyEnvironmentLog, upsertTodayDailyEnvironmentLog } from './api-pressure-manometer-pilot';

export const DEFAULT_GAS_VALIDITY_MONTHS = 6;
const VALIDITY_DAYS_BY_MONTHS = { 3: 90, 6: 181, 12: 365, 24: 730 };

async function currentUserId() {
  return (await supabase.auth.getUser())?.data?.user?.id ?? null;
}

function sanitizePatch(patch = {}) {
  return Object.fromEntries(Object.entries(patch).filter(([, value]) => value !== undefined && !(typeof value === 'number' && !Number.isFinite(value))));
}

export function resolveGasValidityDays(months = DEFAULT_GAS_VALIDITY_MONTHS) {
  const value = Number(months);
  return VALIDITY_DAYS_BY_MONTHS[value] ?? Math.round((365 / 12) * (Number.isFinite(value) && value > 0 ? value : DEFAULT_GAS_VALIDITY_MONTHS));
}

export function addGasValidityDaysToIsoDate(isoDate, months = DEFAULT_GAS_VALIDITY_MONTHS) {
  const base = String(isoDate || '').slice(0, 10);
  if (!base) return null;
  const date = new Date(`${base}T12:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  date.setDate(date.getDate() + resolveGasValidityDays(months));
  return date.toISOString().slice(0, 10);
}

async function getTodayEnvironment() {
  const { getTodayDailyEnvironmentLog } = await import('./api-pressure-manometer-pilot');
  try { return await getTodayDailyEnvironmentLog(); } catch { return null; }
}

export async function createGasSession({
  instrumentId,
  serviceOrderId = null,
  serviceOrderCode = null,
  certificateType = 'NO_SAC',
  validityMonths = DEFAULT_GAS_VALIDITY_MONTHS,
  templateCode = '',
}) {
  if (!instrumentId) throw new Error('instrumentId es requerido');
  const { data: instrument, error: instrumentError } = await supabase
    .from('instruments')
    .select(`id, tag, lab_id, brand, model, serial, type, unit, resolution, range_min, range_max, tolerance, calibration_place,
             category:category_id ( name ),
             client:client_id ( id, client_number, name, address, locality, province )`)
    .eq('id', instrumentId)
    .single();
  if (instrumentError) throw instrumentError;

  const profile = inferGasProfile(instrument, templateCode);
  const environment = await getTodayEnvironment();
  const familyData = mergeGasFamilyData({
    ...DEFAULT_GAS_FAMILY_DATA,
    profile_mode: profile.profileMode,
    channels: profile.channels,
    environment_readings: DEFAULT_GAS_FAMILY_DATA.environment_readings.map((row) => ({
      ...row,
      temp_c: finiteOrNull(environment?.temp_c),
      pressure_hpa: finiteOrNull(environment?.pressure_hpa),
      humidity_rh: finiteOrNull(environment?.humidity_rh),
    })),
  });
  const calibrationDate = new Date().toISOString().slice(0, 10);
  const payload = {
    family: 'gas',
    instrument_id: instrument.id,
    service_order_id: serviceOrderId,
    service_order_code: serviceOrderCode || null,
    certificate_type: certificateType,
    status: 'draft',
    template_code: profile.code,
    template_revision: profile.revision,
    client_number: instrument?.client?.client_number ? String(instrument.client.client_number) : null,
    client_name: instrument?.client?.name || null,
    client_address: instrument?.client?.address || null,
    client_city: instrument?.client?.locality || null,
    client_province: instrument?.client?.province || null,
    instrument_object: instrument?.category?.name || instrument?.type || profile.title,
    instrument_brand: instrument?.brand || null,
    instrument_model: instrument?.model || null,
    instrument_serial: instrument?.serial || null,
    instrument_tag: instrument?.tag || null,
    instrument_unit: 'varios',
    instrument_resolution: finiteOrNull(instrument?.resolution),
    instrument_range_min: finiteOrNull(instrument?.range_min),
    instrument_range_max: finiteOrNull(instrument?.range_max),
    instrument_class: finiteOrNull(instrument?.tolerance),
    instrument_location: instrument?.calibration_place || 'Laboratorio',
    ambient_pressure_avg: finiteOrNull(environment?.pressure_hpa),
    ambient_temp_avg: finiteOrNull(environment?.temp_c),
    ambient_humidity_avg: finiteOrNull(environment?.humidity_rh),
    calibration_date: calibrationDate,
    validity_months: Number(validityMonths) || DEFAULT_GAS_VALIDITY_MONTHS,
    valid_until: addGasValidityDaysToIsoDate(calibrationDate, validityMonths),
    family_data: familyData,
    created_by: await currentUserId(),
  };
  const { data: session, error } = await supabase.from('calibration_sessions').insert(payload).select('*').single();
  if (error) throw error;
  const { error: blockError } = await supabase.from('calibration_session_blocks').insert({
    ...gasBlock(),
    session_id: session.id,
  });
  if (blockError) throw blockError;
  return session;
}

export async function listGasStandardOptions(onDate) {
  const { data, error } = await supabase.rpc('list_gas_standard_options', {
    p_on_date: onDate || new Date().toISOString().slice(0, 10),
  });
  if (error) throw error;
  return data || [];
}

export async function validateGasStandard(sessionId) {
  const { data, error } = await supabase.rpc('validate_gas_session_standard', { p_session_id: sessionId });
  if (error) throw error;
  return data || { valid: false, issues: ['No se pudo validar la trazabilidad.'], configuration: {} };
}

export async function configureGasStandard(sessionId, capabilityId) {
  const { data, error } = await supabase.rpc('configure_gas_session_standard', {
    p_session_id: sessionId,
    p_reference_capability_id: capabilityId,
  });
  if (error) throw error;
  return data;
}

export async function getGasSession(sessionId) {
  const { data, error } = await supabase.from('calibration_sessions').select('*').eq('id', sessionId).eq('family', 'gas').single();
  if (error) throw error;
  return data;
}

export async function updateGasSession(sessionId, patch) {
  const normalized = sanitizePatch(patch);
  if (normalized.family_data) normalized.family_data = mergeGasFamilyData(normalized.family_data);
  const { data, error } = await supabase.from('calibration_sessions').update({
    ...normalized,
    updated_at: new Date().toISOString(),
    updated_by: await currentUserId(),
  }).eq('id', sessionId).eq('family', 'gas').select('*').single();
  if (error) throw error;
  return data;
}

export async function listGasSessionsByInstrument(instrumentId) {
  const { data, error } = await supabase.from('calibration_sessions').select('*')
    .eq('instrument_id', instrumentId).eq('family', 'gas').order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getGasBlock(sessionId) {
  const { data, error } = await supabase.from('calibration_session_blocks').select('*')
    .eq('session_id', sessionId).eq('block_key', 'gas_response').single();
  if (error) throw error;
  return data;
}

export async function listGasPoints(sessionId) {
  const { data, error } = await supabase.from('calibration_session_points').select('*')
    .eq('session_id', sessionId).order('point_no').order('series_no');
  if (error) throw error;
  return data || [];
}

export async function listGasResults(sessionId) {
  const { data, error } = await supabase.from('calibration_session_results').select('*')
    .eq('session_id', sessionId).order('point_no');
  if (error) throw error;
  return data || [];
}

export function gasPointsToRows(points = [], session = {}) {
  const familyData = mergeGasFamilyData(session.family_data);
  const rows = familyData.channels.filter((channel) => channel.enabled).map((channel, index) => ({
    point_no: index + 1,
    analyte_key: channel.key,
    ...Object.fromEntries(Array.from({ length: familyData.repeat_count }, (_, repeat) => [`reading_${repeat + 1}`, ''])),
  }));
  for (const point of points) {
    const row = rows.find((item) => item.analyte_key === String(point.direction || '').toUpperCase()) || rows[Number(point.point_no) - 1];
    if (!row) continue;
    row[`reading_${Number(point.series_no) || 1}`] = point.instrument_reading ?? '';
  }
  return rows;
}

export async function saveGasPoints(sessionId, block, rows = [], familyData = {}) {
  const merged = mergeGasFamilyData(familyData);
  const snapshot = merged.standard_configuration?.reference;
  const payload = [];
  rows.forEach((row) => {
    const certified = (snapshot?.results || []).find((result) => String(result?.metadata?.analyte_key || '').toUpperCase() === row.analyte_key);
    const reference = finiteOrNull(certified?.reference_value) ?? finiteOrNull(certified?.indicated_value);
    const channel = merged.channels.find((item) => item.key === row.analyte_key);
    for (let repeat = 1; repeat <= merged.repeat_count; repeat += 1) {
      const reading = finiteOrNull(row[`reading_${repeat}`]);
      if (reading === null) continue;
      payload.push({
        session_id: sessionId,
        block_id: block.id,
        point_no: Number(row.point_no),
        series_no: repeat,
        direction: row.analyte_key,
        setpoint: reference,
        pattern_reading: reference,
        instrument_reading: reading,
        unit: channel?.unit || certified?.unit || null,
      });
    }
  });
  const { error: deleteError } = await supabase.from('calibration_session_points').delete().eq('session_id', sessionId);
  if (deleteError) throw deleteError;
  if (!payload.length) return [];
  const { data, error } = await supabase.from('calibration_session_points').insert(payload).select('*');
  if (error) throw error;
  return data || [];
}

export async function calculateGasResults(sessionId) {
  const validation = await validateGasStandard(sessionId);
  if (!validation?.valid) throw new Error((validation?.issues || ['La trazabilidad no es válida.']).join(' '));
  const [session, block, points] = await Promise.all([getGasSession(sessionId), getGasBlock(sessionId), listGasPoints(sessionId)]);
  const snapshot = validation.configuration?.reference || null;
  const familyData = mergeGasFamilyData({ ...session.family_data, standard_configuration: { reference: snapshot } });
  const rows = gasPointsToRows(points, { ...session, family_data: familyData });
  const issues = validateGasRows(rows, familyData);
  if (issues.length) throw new Error(issues.join(' '));
  const details = rows.map((row) => computeGasChannelMetrics(row, { ...session, family_data: familyData }, snapshot, block));
  if (details.some((item) => !item)) throw new Error('El certificado del gas patrón no cubre todos los canales seleccionados.');
  const results = details.map((metrics) => ({
    session_id: sessionId,
    block_id: block.id,
    point_no: metrics.point_no,
    pattern_mean: metrics.pattern_mean,
    instrument_mean: metrics.instrument_mean,
    error_abs: metrics.error_abs,
    error_pct: metrics.error_pct,
    hysteresis: null,
    repeatability: metrics.repeatability,
    u_standard: metrics.u_standard,
    u_drift: 0,
    u_resolution: metrics.u_resolution,
    u_repeatability: metrics.u_repeatability,
    u_temp: metrics.u_temp,
    u_other: Math.hypot(metrics.u_flow, metrics.u_other),
    uc: metrics.uc,
    k: metrics.k,
    u_expanded: metrics.u_expanded,
    u_reported: metrics.u_reported,
    in_tolerance: metrics.in_tolerance,
  }));
  const { error: deleteError } = await supabase.from('calibration_session_results').delete().eq('session_id', sessionId);
  if (deleteError) throw deleteError;
  const { error } = await supabase.from('calibration_session_results').insert(results);
  if (error) throw error;
  await updateGasSession(sessionId, {
    status: 'calculated',
    standard_serial: familyData.cylinder_number,
    family_data: mergeGasFamilyData({ ...familyData, result_details: details }),
  });
  return results;
}
